import React, {useEffect, useRef} from 'react';

import './custom-modal-v2.scss';
import {useSelector} from "react-redux";
import users from "../../users";
import {cn} from "../../../utils/utils";
import {createPortal} from "react-dom";

type ModalBtnProps = {
    text: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    disabled?: boolean;
}

type CustomModalV2Props = {
    title?: React.ReactNode;
    children: React.ReactNode;
    open: boolean;
    onClose?: () => void;
    closeOnBackdropClick?: boolean;
    closeOnEscape?: boolean;
    primaryButton?: ModalBtnProps;
    secondaryButton?: ModalBtnProps;
    tertiaryButton?: ModalBtnProps;
    className?: string;
    footerLeftContent?: React.ReactNode;
    customContent?: boolean;
    zIndex?: number;
}

const defaultProps = {
    closeOnBackdropClick: true,
    closeOnEscape: true,
}

const CustomModalV2 = (props: CustomModalV2Props) => {
    const {
        title,
        children,
        open,
        onClose,
        tertiaryButton,
        primaryButton,
        secondaryButton,
        closeOnEscape: closeOnEscapeProp,
        closeOnBackdropClick: closeOnBackdropClickProp
    } = props;

    const closeOnEscape = closeOnEscapeProp ?? defaultProps.closeOnEscape;
    const closeOnBackdropClick = closeOnBackdropClickProp ?? defaultProps.closeOnBackdropClick;
    const darkMode = useSelector(users.selectors.isDarkMode)

    // Portal container
    const elRef = useRef<HTMLDivElement | null>(null);
    if (typeof document !== 'undefined' && !elRef.current) {
        elRef.current = document.createElement('div');
        elRef.current.className = 'custom-modal-v2-portal';
    }

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && open && closeOnEscape) {
                if (onClose) onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, onClose, closeOnEscape]);

    useEffect(() => {
        const el = elRef.current;
        if (!el) return;
        document.body.appendChild(el);
        return () => {
            if (document.body.contains(el)) document.body.removeChild(el);
        };
    }, []);

    if (!elRef.current) return null;

    const backdropComponent = () => {
        return (
            <div className="custom-modal-v2-backdrop" style={{
                opacity: open ? 1 : 0,
                pointerEvents: open ? 'auto' : 'none',
                transition: 'opacity 0.3s ease',
                zIndex: props.zIndex ?? 11,
            }} onClick={() => {
                if (onClose && closeOnBackdropClick) onClose();
            }}>
            </div>
        )
    }

    if (props.customContent) {
        const modalContent = (
            <>
                {backdropComponent()}
                <div className={cn("custom-modal-v2-custom-content", props.className)} style={{
                    zIndex: props.zIndex ?? 12,
                    opacity: open ? 1 : 0,
                    pointerEvents: open ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}>
                    {children}
                </div>
            </>
        )

        return createPortal(modalContent, elRef.current);
    }

    const modalContent = (
        <>
            {backdropComponent()}
            <div className={cn("custom-modal-v2-content", {"custom-modal-v2-content-dark": darkMode})} style={{
                opacity: open ? 1 : 0,
                pointerEvents: open ? 'auto' : 'none',
                transition: 'opacity 0.3s ease',
                zIndex: props.zIndex ?? 12,
            }}>
                {title && <div className="custom-modal-v2-header">
                    <h5 className="custom-modal-v2-header-title">{title}</h5>
                    {onClose && open &&
                        <button type="button" className="custom-modal-v2-header-close-btn" onClick={onClose}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>}
                </div>}
                <div className={cn("custom-modal-v2-body", props.className)}>
                    {children}
                </div>
                {(primaryButton || secondaryButton) &&
                    <div className="custom-modal-v2-footer">
                        {props.footerLeftContent &&
                            <div className="custom-modal-v2-footer-left-content">
                                {props.footerLeftContent}
                            </div>
                        }
                        {tertiaryButton &&
                            <button
                                className={`custom-modal-v2-footer-btn custom-modal-v2-footer-btn--${tertiaryButton.variant ?? 'secondary'}`}
                                onClick={tertiaryButton.onClick}
                                disabled={tertiaryButton.disabled}
                            >
                                {tertiaryButton.text}
                            </button>
                        }
                        {secondaryButton &&
                            <button
                                className={`custom-modal-v2-footer-btn custom-modal-v2-footer-btn--${secondaryButton.variant ?? 'secondary'}`}
                                onClick={secondaryButton.onClick}
                                disabled={secondaryButton.disabled}
                            >
                                {secondaryButton.text}
                            </button>
                        }
                        {primaryButton &&
                            <button
                                className={`custom-modal-v2-footer-btn custom-modal-v2-footer-btn--${primaryButton.variant ?? 'primary'}`}
                                onClick={primaryButton.onClick}
                                disabled={primaryButton.disabled}
                            >
                                {primaryButton.text}
                            </button>
                        }
                    </div>
                }
            </div>
        </>
    )

    return createPortal(modalContent, elRef.current);
}

export default CustomModalV2;
